/* eslint-disable jsdoc/require-param */
/** @import { z } from 'zod' */

/**
 * Fetcher intended for JSON payloads
 *
 * This fetcher is intended for payloads where the data is not necessarily backed by some
 * type guarantee, like that of GraphQL. As a result, the fetcher takes a zod schema to allow
 * us to properly verify and provide defaults for the response data.
 * @template {z.ZodTypeAny} T
 * @param {T} schema A Zod schema that defines the shape of the data you expect to receive
 * @param {RequestInit} [options] An optional RequestInit object that contains any custom settings that you want to apply to the request
 * @returns {(key: string | URL) => Promise<z.output<T>>}
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/fetch for more information on the resource and options params
 * that will be passed to `fetch` as the URL to fetch.
 */
export function fetcher(schema, options) {
  return function safeFetcher(key) {
    return fetch(key, { ...options, signal: AbortSignal.timeout(5000) })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Network response was not ok: ${res.status} ${res.statusText}`);
        }
        return res;
      })
      .then((res) => res.json())
      .then((json) => schema.parse(json))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error fetching data:', error);
        return Promise.reject(error);
      });
  };
}
